import type { PublicProductSummary } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { storeProductPath } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';

interface FeaturedCopyProps {
  readonly product: PublicProductSummary;
}

/** 소개 줄 — 실제로 있는 사실(색상 수·인쇄 방식)과 서비스의 제작 방식만 쓴다. */
const descriptionLines = (product: PublicProductSummary): readonly string[] =>
  [
    product.availableColorCount > 0
      ? `색상 ${formatCount(product.availableColorCount)}종 중에서 고를 수 있습니다.`
      : null,
    product.printMethodNames.length > 0
      ? `인쇄 방식: ${product.printMethodNames.join(', ')}`
      : null,
    '상품 사진 위에서 디자인하고, 상담 후 확정견적으로 제작합니다.',
  ].filter((line): line is string => line !== null);

/** 이달의 상품 왼쪽 문구 — 큰 상품명, 소개 줄, 최소 수량·시작 가격, 상세 보기. 장이 바뀌면 떠오르며 나타난다. */
export const FeaturedCopy = ({ product }: FeaturedCopyProps): ReactElement => (
  <div className="mt-5 flex flex-col gap-8 motion-safe:animate-fade-up">
    <h3 className="text-[clamp(2.25rem,4.4vw,4rem)] leading-[1.15] font-bold tracking-[-0.02em]">
      {product.name}
    </h3>
    <ul className="flex flex-col gap-1.5 text-base text-white/85 sm:text-lg">
      {descriptionLines(product).map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
    <div className="flex flex-wrap items-center gap-3">
      <span className="bg-brand-lime px-2 py-0.5 text-sm font-bold text-black tabular-nums">
        최소 {formatCount(product.minOrderQty)}개
      </span>
      <span className="text-3xl font-bold tabular-nums">
        {formatKrw(product.baseUnitPrice)}부터
      </span>
    </div>
    <Link
      to={storeProductPath(product.id)}
      className="inline-flex h-12 items-center self-start rounded-full border border-white/80 px-6 text-[15px] font-medium transition-colors hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      자세히 보기
    </Link>
  </div>
);
